(ns frontend.view.entity-viewers.events.editor-utils
  (:require
   [clojure.set :as set]
   [common.entity :as entity]
   [common.features.plan :as plan]
   [common.localization :as loc]
   [common.schema.field :as field]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.components.field-editor :as field-editor]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.activity.insider-activity :as insider-activity]
   [frontend.view.modals.attachments.view :as attachments]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.ck-editor.view :as ck-editor]
   [ui.components.loading-spinner.view :as spinner]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.rich-text.view :as rich-text]
   [ui.dom-globals :as dg]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]))

(defn update-draft
  [draft-atom viewed-entity-id editor-type attribute value]
  (swap! draft-atom assoc attribute value)
  (when viewed-entity-id
    (ls/update-in [:activity viewed-entity-id editor-type] assoc attribute value)))

(defn custom-toolbar-button
  [entity-id state]
  (let [entity (session/get-entity entity-id)
        attachments (when (not= :entity.type/note (:entity/type entity))
                      (:common/attachments entity))]
    [:div
     {:on-click (fn [_e]
                  (modals/open-modal!
                   attachments/modal-key
                   {:entity-attachments attachments
                    :on-primary-submit! (fn [files]
                                          (swap! state assoc
                                                 :common/attachments
                                                 (set (concat (:common/attachments @state) files))))}))}
     [:i.fal.fa-paperclip]]))

(defn guest-and-not-allowed-field?
  [field-attr]
  (and (session/is-guest?)
       (some #{field-attr}
             [:common/related-entities
              :common/related-entities-oppo
              :common/related-entities-person
              :common/related-entities-company
              :common/related-entities-investor
              :common/related-entities-fund
              :common/related-entities-fundraising-opportunities
              :common/related-entities-sales-opportunities
              :common/related-entities-holdings
              :common/related-entities-sale-agreements
              :common/related-entities-real-estate
              :common/related-entities-fof-asset
              :common/related-entities-other-asset
              :common/related-entities-fund-commitment])))

(defn no-related-entities?
  [field-attr]
  ;; remove if feature not enabled or empty choices
  (case field-attr
    :common/related-entities-fund-commitment
    (or (not (session/fundraising?))
        (session/no-known-entities? :entity.type/fund-commitment @session/all-fund-commitments))
    :common/related-entities-fund
    (or (not (session/fundraising?))
        (not (session/has-feature-access? :feature/fund-management))
        (session/no-known-entities? :entity.type/fund @session/all-funds))
    :common/related-entities-fundraising-opportunities
    (or (not (session/fundraising?))
        (session/no-known-entities? :entity.type/fundraising-opportunity @session/all-fundraising-opportunities))
    :common/related-entities-sales-opportunities
    (or (not (session/sales-pipelines?))
        (session/no-known-entities? :entity.type/sales-opportunity @session/all-sales-opportunities))
    :common/related-entities-holdings
    (or (not (session/portfolio?))
        (session/no-known-entities? :entity.type/holding @session/all-holdings))
    :common/related-entities-sale-agreements
    (or (not (session/sales-pipelines?))
        (session/no-known-entities? :entity.type/sale-agreement @session/all-sale-agreements))
    :common/related-entities-investor
    (or (and (not (session/portfolio?))
             (not (session/fundraising?)))
        (session/no-known-entities? :entity.type/investor @session/all-investors))
    :common/related-entities-oppo
    (or (not (session/dealflow?))
        (session/no-known-entities? :entity.type/oppo @session/all-oppos))
    :common/related-entities-real-estate
    (or (not (plan/available :feature/asset-types-real-estate @session/tenant))
        (session/no-known-entities? :entity.type/real-estate @session/all-real-estates))
    :common/related-entities-fof-asset
    (or (not (plan/available :feature/asset-types-fof @session/tenant))
        (session/no-known-entities? :entity.type/fof-asset @session/all-fof-assets))
    :common/related-entities-other-asset
    (or (not (plan/available :feature/asset-types-other @session/tenant))
        (session/no-known-entities? :entity.type/other-asset @session/all-other-assets))
    ;;no need for company, oppo, person?
    false))

(defn resolve-activity-template
  [{:keys [raw-schema editable-id entity-type]}]
  (some->>
   raw-schema
   (remove (fn [[attribute field-schema]]
             (or (no-related-entities? attribute)
                 (guest-and-not-allowed-field? attribute)
                 (not (f-permissions/field-write-access? attribute entity-type nil))
                 (->> field-schema
                      :field/default-visibility
                      :editor
                      (= :editor.type/none)))))
   (map (fn [[attribute field-schema]]
          (merge field-schema
                 {:attribute   attribute
                  :access/type (if editable-id
                                 (f-permissions/access-to-field-long attribute entity-type nil)
                                 :field.access/write)
                  :editor/type (->> field-schema
                                    :field/default-visibility
                                    :editor)}
                 (when (and (some #{attribute}
                                  [:activity/topic
                                   :activity/tag-tags
                                   :activity/event])
                            (not (session/is-admin?))
                            (tenant-customizations/tesi?))
                   {:create-on-fly? false}))))
   (cu/remove-by :editor/type :editor.type/none)
   (remove :field/secondary-field?)                                                                 ;; secondary fields are shown in related fields in editor
   ))

(defn- hydrate-picked-entities!
  "Calls `on-done` when each entity in `picked-value` is in the session. The
   function fetches the entities that are not in the session.

   An association picker returns ids from the search index. A new-grid account does
   not have those entities in the session. The field reads its value from the
   session to show and filter it (`field/particular-value`). A new picked entity
   would be saved but not shown. An old-grid account has the entities already, so
   the function does nothing there."
  [{:keys [picked-value on-done]}]
  (let [missing (->> (if (coll? picked-value) picked-value [picked-value])
                     (filter number?)
                     (remove (fn [id] (contains? @session/entities id)))
                     (distinct))]
    (if (empty? missing)
      (on-done)
      (let [remaining (atom (count missing))]
        (doseq [id missing]
          (entity-scope/hydrate! {:entity-id id
                                  :on-done   (fn []
                                               (when (zero? (swap! remaining dec))
                                                 (on-done)))}))))))

(defn on-save-metadata!
  [{:keys [field-schema update-draft! editable-id entity-atom local-atom editor-new-value
           on-success!]}]
  (let [{:keys [attribute field/particular-value field/aggregate-attribute]} field-schema
        entity (if editable-id
                 (merge @entity-atom @local-atom)
                 @entity-atom)
        old-particular-value (if (and aggregate-attribute (fn? particular-value))
                               (set (particular-value (get entity aggregate-attribute)
                                                      @session/entities))
                               (get entity attribute))
        edited-value (if-not aggregate-attribute
                       editor-new-value
                       (let [new-value (set editor-new-value)
                             removed-values (set/difference old-particular-value new-value)]
                         (if-not aggregate-attribute
                           new-value
                           (-> (if editable-id
                                 (get @local-atom aggregate-attribute)
                                 (get entity aggregate-attribute))
                               (set/union new-value)
                               (set/difference removed-values)))))
        save-attribute (or aggregate-attribute attribute)]
    (if editable-id
      (do (swap! local-atom assoc save-attribute edited-value)
          (deref local-atom))
      (do
        (update-draft! save-attribute edited-value)
        (on-success!)))))

(defn resolve-value
  [{:keys [aggregate-attribute particular-value entity attribute]}]
  (cond (and aggregate-attribute (fn? particular-value))
        (set (particular-value (get entity aggregate-attribute) session/get-entity))
        (= :activity/tag-tags attribute)
        (set (get entity attribute))                                                                ;; isn't this always set ?
        :else
        (get entity attribute)))

(defn attachments-block
  [state]
  (let [attachments (:common/attachments @state)]
    (when (> (count attachments) 0)
      [:ul
       {:class "pt-3 mt-3 flex flex-wrap border-t border-solid border-base-light-border-paler"}
       (doall
        (for [id attachments]
          ^{:key (str "notes-attachment-" id)}
          [:li
           {:class "flex items-center gap-3"}
           [:i.fal.fa-paperclip]
           [:span (entity/display-string (or (session/get-entity id) id))]
           [:i.fal.fa-times
            {:on-click (fn [_]
                         (swap! state assoc
                                :common/attachments
                                (->> @state
                                     :common/attachments
                                     (remove (fn [aid]
                                               (= id aid)))
                                     (set))))}]]))])))

(defn metadata-view
  [{:keys [_entity-atom _local-atom _ref-atom _metadata-template _mandatory-field? _editable-id
           _update-draft! _full-width?]}]
  (let [state-atom (r/atom {})]
    (r/create-class
     {:display-name
      "Activity entity metadata editor"
      :reagent-render
      (fn [{:keys [entity-atom local-atom ref-atom metadata-template
                   mandatory-field? editable-id update-draft! full-width?] :as ctx}]
        (let [entity (if editable-id
                       (merge @entity-atom @local-atom)
                       @entity-atom)
              show-all? (:show-all? @state-atom)
              show-editor? (fn [value aggregate-attribute]
                             (not (and (fu/empty-value? value)
                                       (not show-all?)
                                       aggregate-attribute)))
              field-visible? (fn [{:keys [field/in-use-for-this-data? attribute
                                          field/aggregate-attribute field/particular-value]}]
                               (let [value (resolve-value {:aggregate-attribute aggregate-attribute
                                                           :particular-value    particular-value
                                                           :entity              entity
                                                           :attribute           attribute})]
                                 (and (or (not (fn? in-use-for-this-data?))
                                          (in-use-for-this-data? entity
                                                                 @session/entity-types
                                                                 session/has-feature-access?
                                                                 session/has-entity-access?))
                                      (show-editor? value aggregate-attribute))))
              full-template metadata-template
              metadata-template (->> metadata-template
                                     (filter field-visible?))
              some-aggregate-fields-empty?
              (->> full-template
                   (filter :field/aggregate-attribute)
                   (some (fn [{:keys [attribute field/aggregate-attribute field/particular-value]}]
                           (let [value (resolve-value {:aggregate-attribute aggregate-attribute
                                                       :particular-value    particular-value
                                                       :entity              entity
                                                       :attribute           attribute})]
                             (fu/empty-value? value)))))
              field-count (count metadata-template)
              index-to-render-menu-dropdown-top (- field-count 2)]
          [:div
           {:class "pt-2 *:py-0 flex flex-col gap-2"}
           (doall
            (->>
             metadata-template
             (sort-by :field/default-ord)
             (map-indexed
              (fn [idx
                   {:keys [attribute field/aggregate-attribute field/particular-value]
                    :as   field-schema}]
                (let [value (resolve-value
                             {:aggregate-attribute aggregate-attribute
                              :particular-value    particular-value
                              :entity              entity
                              :attribute           attribute})]
                  (when (show-editor? value aggregate-attribute)
                    ^{:key (str "field-editor" editable-id "-" attribute)}
                    [field-editor/view
                     (merge
                      field-schema
                      {:menu-placement        (if (>= idx index-to-render-menu-dropdown-top)
                                                "top"
                                                "auto")
                       :id                    (str "field-editor" editable-id "-" attribute)
                       :drag-options          {:hide-drag-area? true}
                       :state-atom            state-atom
                       :value                 value
                       :containing-entity     entity
                       :full-width?           full-width?
                       :close-menu-on-scroll? true
                       :menu-portal-target    (dg/element-by-id "react-select-menu-container")
                       :read-only?            (not (some #{(:access/type field-schema)}
                                                         [:field.access/write :field.access/create]))
                       :entity-types-atom     session/entity-types
                       :label                 (or (->> field-schema
                                                       :field/translations
                                                       :en)
                                                  (loc/field-name attribute field-schema)
                                                  (loc/tr attribute))                                   ;; we select label outside of field-editor so we can customize label if needed
                       :mandatory?            mandatory-field?                                      ;; can be function or boolean - most likely always view dependent
                       :placeholder           (field/placeholder field-schema)
                       :tools                 [:tool-item/cancel]
                       :error-text            nil                                                   ;; TODO: use editor-atom to store validation and save errors
                       :on-save!              (fn [_attribute editor-new-value on-success! _on-error!]
                                                (hydrate-picked-entities!
                                                 {:picked-value editor-new-value
                                                  :on-done      (fn []
                                                                  (on-save-metadata!
                                                                   (assoc ctx
                                                                     :field-schema field-schema
                                                                     :editor-new-value editor-new-value
                                                                     :on-success! on-success!)))}))})]))))))
           (when some-aggregate-fields-empty?
             [:div
              {:class "*:p-0"}
              [button/primary-open
               {:on-click (fn [e]
                            (swap! state-atom update :show-all? not)
                            (dg/prevent-and-stop e)
                            (oops/ocall @ref-atom "scrollIntoView"))}
               (loc/tr (if show-all?
                     :hide-empty
                     :show-all))]])

           (when (session/insider-groups?)
             (let [insider-groups (->> @session/all-insider-groups
                                       (remove :common/disabled?))
                   active-insider-groups (->> insider-groups
                                              (cu/filter-by :insider-group/status :insider-group.status/active)
                                              (seq))
                   ]
               (when active-insider-groups
                 (let [selected-group-id (if editable-id
                                           (:common/insider-group @local-atom)
                                           (:common/insider-group @entity-atom))
                       selected-group (session/get-entity selected-group-id)
                       options (->> active-insider-groups
                                    (map (fn [ai]
                                           {:label (:insider-group/name ai)
                                            :value (:db/id ai)}))
                                    (sort-by :label)
                                    (vec))
                       set-insider-group! (fn [val]
                                            (if editable-id
                                              (swap! local-atom assoc :common/insider-group val)
                                              (update-draft! :common/insider-group val)))]
                   [:div
                    [labelled-input/view
                     {:label         (loc/tr :common/insider-group)
                      :editor-type   :editor.type/select
                      :is-clearable? true
                      :value         (->> options
                                          (cu/filter-by :value selected-group-id)
                                          (first))
                      :on-change     (fn [val]
                                       (if (and (nil? val) selected-group-id)
                                         (modals/open-modal!
                                          confirmation/modal-key
                                          {:title         (loc/tr :clear-insider-group)
                                           :body          (loc/tr :clear-insider-group-confirmation)
                                           :checkbox-text (loc/tr :clear-insider-group-checkbox)
                                           :confirm-text  (loc/tr :clear)
                                           :on-confirm    #(set-insider-group! nil)})
                                         (set-insider-group! val)))
                      :options       options}]
                    (when selected-group
                      [popover/view
                       {:open?          (:popover-open? @state-atom)
                        :on-open-change (fn [open?]
                                          (swap! state-atom assoc :popover-open? open?))}
                       [:div
                        {:class "*:px-0"}
                        [button/secondary-open
                         {:class    "underline"
                          :on-click (fn [e]
                                      (swap! state-atom assoc :popover-open? (not (:popover-open? @state-atom)))
                                      (fu/stop-n-prevent e))}
                         (loc/tr :show-insiders)]]
                       [insider-activity/popup-view selected-group-id]])]))))]))})))

(defn attachment-button
  [entity-id state]
  (let [entity (session/get-entity entity-id)
        attachments (when (not= :entity.type/note (:entity/type entity))
                      (:common/attachments entity))]
    (r/as-element
     [:div
      {:class    (str "rdw-option-wrapper")
       :on-click (fn [_e]
                   (modals/open-modal!
                    attachments/modal-key
                    {:entity-attachments attachments
                     :on-primary-submit! (fn [files]
                                           (swap! state assoc
                                                  :common/attachments
                                                  (set (concat (:common/attachments @state) files))))}))}
      [:i.fal.fa-paperclip]])))

(defn wysiwyg-message-editor
  [{:keys [_always-expanded? _wysiwyg-attribute _activity-entity-id
           _entity-atom _local-atom _activity-entity-type
           _init-draft-state! _draft-update!]}]
  (let [wysiwyg-atom (r/atom {})]
    (fn
      [{:keys [always-expanded? wysiwyg-attribute activity-entity-id
               entity-atom local-atom activity-entity-type
               init-draft-state! draft-update!]}]
      (let [content (or (get @entity-atom wysiwyg-attribute) "")
            expanded? (or always-expanded? (not (rich-text/empty-editor-value? content)))

            note-editor? (= :note activity-entity-type)
            show-attachments? (and note-editor?
                                   (seq (:common/attachments @entity-atom)))]
        [:div
         {:class (uu/cm "flex-auto"
                        (when (:wysiwyg-focus @wysiwyg-atom) "")
                        (when expanded? ""))}
         [:div
          {:class-name "h-full flex flex-col oveflow-hidden *:border-0 [&_.ck-content]:border-0 [&_.ck-focused]:border"}
          [ck-editor/editor
           {:value            content
            :on-focus         #(swap! wysiwyg-atom assoc :wysiwyg-focus true)
            :on-blur          #(swap! wysiwyg-atom assoc :wysiwyg-focus false)
            :class            "text"
            :toolbar-position (cond (not expanded?)
                                    :hidden
                                    :else
                                    :bottom)
            :on-change        (fn [value]
                                (let [content (or (get @entity-atom wysiwyg-attribute) "")
                                      had-content? (not (rich-text/empty-editor-value? content))]
                                  (swap! wysiwyg-atom assoc :wysiwyg-updates? true)
                                  (if activity-entity-id
                                    (swap! local-atom assoc wysiwyg-attribute value)
                                    (do
                                      (when (and (not always-expanded?)
                                                 (not (rich-text/empty-editor-value? value))        ;; new value
                                                 (not had-content?))                                ;; did not have content
                                        ;; initialize related entities on first input
                                        (init-draft-state! draft-update!))
                                      (draft-update! wysiwyg-attribute value)))))}]
          (when show-attachments?
            [attachments-block entity-atom])]]))))

(defn cancel-button
  [cancel!]
  [button/sm-secondary-hollow
   {:on-click #(cancel! %)}
   (loc/tr :cancel)])

(defn submit-button
  [{:keys [submit-button-text disabled? disabled-text on-click!]}]
  (cond (and disabled? (not (cu/blank-value? disabled-text)))
        [button/sm-primary-solid
         {:tooltip disabled-text
          :disabled? true}
         submit-button-text]
        disabled?
        [button/sm-primary-solid
         {:disabled? true}
         submit-button-text]
        :else
        [button/sm-primary-solid
         {:on-click on-click!}
         submit-button-text]))

(defn update-existing-activity!
  [{:keys [entity-atom local-atom wysiwyg-attribute activity-schema on-reset! on-success-callback!]}]
  (let [entity-id (:db/id @entity-atom)
        data (atom (merge {}
                          (when (get @local-atom wysiwyg-attribute)
                            {wysiwyg-attribute (get @local-atom wysiwyg-attribute)})
                          (let [old-ig (:common/insider-group @entity-atom)
                                new-ig (:common/insider-group @local-atom)]
                            (cond
                              (and (some? old-ig) (nil? new-ig))
                              {:common/insider-group nil}
                              (and (some? new-ig) (not= old-ig new-ig))
                              {:common/insider-group new-ig}))
                          (when-let [attachments (:common/attachments @entity-atom)]
                            {:common/attachments attachments})))]
    (doseq [fld activity-schema]
      (let [attrib (or (:field/aggregate-attribute fld) (:attribute fld))
            updated-value (get @local-atom attrib)
            old-value (get @entity-atom attrib)]
        (when-not (contains? @data attrib)                                                          ;; aggregate attributes are not dealt multiple times
          (cond
            (and (not (nil? old-value)) (nil? updated-value)) (swap! data assoc attrib nil)         ;; clear value
            (not= old-value updated-value) (swap! data assoc attrib updated-value)                  ;; update
            ))))
    (if (empty? @data)
      (swap! local-atom dissoc :processing)
      (entity-actions/*->entity-update! entity-id
                                        @data
                                        (fn [resp]
                                          (when on-reset!
                                            (on-reset! true)
                                            (swap! local-atom dissoc :processing))
                                          (when (fn? on-success-callback!)
                                            (on-success-callback! resp)))
                                        (fn [& err]
                                          (swap! local-atom dissoc :processing)
                                          (logging/log "Failed : " err))))))

(defn processing-view
  []
  [:div
   {:class (uu/cm "absolute left-0 right-0 top-0 bottom-0 flex justify-center items-center bg-black/[.35] z-100" "")}
   [:div
    {:class "flex flex-col justify-center items-center gap-2 relative z-10"}
    [:div
     {:class "*:w-12 *:h-12"}
     [spinner/circle]]
    [:span (loc/tr :processing)]]])

(defn component
  [{:keys [activity-entity-id activity-entity-type externally-passed-entity entity-schema viewed-entity-id
           init-draft-state!
           _wysiwyg-attribute _always-expanded? _modal? _on-submit! _mandatory-field? _custom-delete-button
           _full-width? _submit-button-disabled? _submit-button-text _disabled-button-tooltip-text
           _on-reset! _on-success-callback!]}]
  (let [original-entity (cond
                          externally-passed-entity
                          externally-passed-entity

                          activity-entity-id
                          (get @session/entities activity-entity-id)

                          :else nil)
        local-atom (r/atom (if original-entity
                             original-entity
                             {}))
        local-storage-data (when viewed-entity-id
                             (ls/get-in [:activity viewed-entity-id activity-entity-type]))
        draft-atom (r/atom (merge {} @local-atom local-storage-data))
        ref-atom (r/atom nil)
        entity-atom
        (if activity-entity-id (r/cursor session/entities [activity-entity-id]) draft-atom)
        draft-update! (partial update-draft draft-atom viewed-entity-id activity-entity-type)
        entity-type (keyword "entity.type" activity-entity-type)
        filtered-metadata-schema (resolve-activity-template {:raw-schema  entity-schema
                                                             :editable-id activity-entity-id
                                                             :entity-type entity-type})]
    (init-draft-state! draft-update!)
    (r/create-class
     {:display-name "Activity entity editor"
      :reagent-render
      (fn
        [{:keys [activity-entity-id activity-entity-type wysiwyg-attribute always-expanded? modal?
                 on-submit! mandatory-field? custom-delete-button full-width?
                 submit-button-disabled? submit-button-text disabled-button-tooltip-text
                 on-reset! on-success-callback!]
          :as   ctx}]
        (let [entity-atom (if activity-entity-id
                            draft-atom
                            entity-atom)
              temp-atom (if activity-entity-id
                          local-atom
                          draft-atom)
              disabled-text (if (fn? disabled-button-tooltip-text)
                              (disabled-button-tooltip-text temp-atom)
                              disabled-button-tooltip-text)
              content (get @entity-atom wysiwyg-attribute)
              has-content? (or always-expanded? (not (rich-text/empty-editor-value? content)))
              cancel! (fn [_e]
                        (reset! local-atom {})
                        (reset! draft-atom {})
                        (when viewed-entity-id
                          (ls/set-in [:activity viewed-entity-id activity-entity-type] {}))
                        (when on-reset!
                          (on-reset! false)))
              no-changes? (when original-entity
                            (= original-entity @local-atom))
              submit-disabled? (or no-changes?
                                   (cond
                                     (fn? submit-button-disabled?)
                                     (submit-button-disabled? (if activity-entity-id
                                                                local-atom
                                                                entity-atom))

                                     (and (fn? disabled-button-tooltip-text)
                                          disabled-text)
                                     true

                                     :else
                                     (empty? (:common/related-entities (if activity-entity-id
                                                                         @local-atom
                                                                         @entity-atom)))))
              submit-clicked!
              (fn [_]
                (swap! local-atom assoc :processing true)
                (if activity-entity-id
                  (update-existing-activity!
                   {:entity-atom          entity-atom
                    :local-atom           local-atom
                    :wysiwyg-attribute    wysiwyg-attribute
                    :activity-schema      filtered-metadata-schema
                    :on-reset!            on-reset!
                    :on-success-callback! on-success-callback!})
                  (on-submit!
                   @draft-atom
                   (fn [_]
                     ;; success
                     (reset! local-atom {})
                     (reset! draft-atom {})
                     (when viewed-entity-id
                       (ls/set-in [:activity viewed-entity-id activity-entity-type] {}))
                     (when on-reset!
                       (on-reset! true))
                     (swap! local-atom dissoc :processing))
                   (fn [err]
                     (swap! local-atom dissoc :processing)
                     (logging/error " Failed to create activity entity : " activity-entity-type
                                    " -> " err)))))]
          [:div
           {:class "flex w-full"}
           (when (:processing @local-atom)
             [processing-view])
           [wysiwyg-message-editor
            (assoc ctx
              :entity-atom entity-atom
              :local-atom local-atom
              :draft-update! draft-update!
              :custom-toolbar-button
              (partial custom-toolbar-button activity-entity-id entity-atom))]
           [:div
            {:class-name (uu/cm "px-2 w-[320px] border-l border-solid border-base-light-border-paler" (when-not has-content? :hidden))
             :ref        (fn [element]
                           (reset! ref-atom element))}
            [metadata-view
             {:local-atom        local-atom
              :entity-atom       entity-atom
              :full-width?       full-width?
              :ref-atom          ref-atom
              :metadata-template filtered-metadata-schema
              :entity-type       entity-type
              :mandatory-field?  mandatory-field?
              :editable-id       activity-entity-id
              :update-draft!     draft-update!}]
            (if custom-delete-button
              [:div
               {:class-name "p-2 grid grid-auto-cols-auto grid-cols-[auto_1fr_1fr] gap-2"}
               custom-delete-button
               [cancel-button cancel!]
               [submit-button
                {:modal?             modal?
                 :disabled?          submit-disabled?
                 :disabled-text      (if no-changes?
                                       ""
                                       disabled-text)
                 :submit-button-text submit-button-text
                 :on-click!          submit-clicked!}]]
              [:div
               {:class-name "py-2 grid grid-cols-2 gap-2"}
               [cancel-button cancel!]
               [submit-button
                {:modal?             modal?
                 :disabled?          submit-disabled?
                 :disabled-text      (if no-changes?
                                       ""
                                       disabled-text)
                 :submit-button-text submit-button-text
                 :on-click!          submit-clicked!}]])]]))})))

(defn editor
  [{:keys [viewed-entity-id _state  _activity-entity-id _externally-passed-entity _entity-id _activity-entity-type
           _wysiwyg-attribute
           _placeholder _always-expanded? _init-draft-state! _entity-schema _mandatory-field? _on-submit!
           _submit-button-text _disabled-button-tooltip-text _on-reset!]
    :as   view-args}]
  ^{:key (str "message-editor-viewer-id-" viewed-entity-id)}
  [component view-args])
